// Project List Component

import type { Project } from '@/lib/types';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';

interface ProjectListProps {
  projects: Project[];
  isLoading: boolean;
  onEdit: (project: Project) => void;
  onShowIntegrationCode: (project: Project) => void;
  onToggleActive: (project: Project) => void;
  onDelete: (project: Project) => void;
}

// Projektart auf einen Blick: Sie entscheidet ueber Berichtskapitel und Wortwahl,
// also soll man sie in der Liste sehen und nicht erst im Formular.
const artLabel: Record<string, string> = {
  landingpage: 'Landingpage + Ads',
  website: 'Komplette Website',
  bewerbungen: 'Mitarbeitergewinnung',
};
const artStil: Record<string, string> = {
  landingpage: 'bg-rose-50 text-rose-700 border-rose-200',
  website: 'bg-blue-50 text-blue-700 border-blue-200',
  bewerbungen: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function ProjectList({
  projects,
  isLoading,
  onEdit,
  onShowIntegrationCode,
  onToggleActive,
  onDelete,
}: ProjectListProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Projekte</h3>
        <p className="mt-1 text-sm text-gray-500">
          Erstellen Sie ein neues Projekt für diesen Kunden
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Projekt
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Art
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Kunde
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Slug
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Aktionen
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {projects.map((project) => (
            <tr key={project.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{project.name}</div>
                  {project.description && (
                    <div className="text-sm text-gray-500">{project.description}</div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${artStil[project.project_type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  {artLabel[project.project_type] || project.project_type || '—'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{project.customer?.name || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">{project.slug}</code>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge variant={project.active ? 'success' : 'secondary'}>
                  {project.active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </td>
              <td className="px-6 py-4 text-right text-sm font-medium">
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onShowIntegrationCode(project)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    📋 Integration
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(project)}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleActive(project)}
                    className={project.active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                  >
                    {project.active ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`Projekt "${project.name}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) {
                        onDelete(project);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                  >
                    🗑️ Löschen
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
